import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useWindowWidth from '../hooks/useWindowWidth';
import { swalSuccess, swalError } from '../utils/alert';
import './ExpenseForm.css';
import './RevenueDesktop.css';

function CustomerForm({ customer, onSuccess }) {
  const [formData, setFormData] = useState(
    customer || {
      name: '',
      trading_name: '',
      document: '',
      phones: [],
      email: '',
      address: {
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zip_code: '',
      },
      observations: '',
      status: 'Ativo',
    }
  );
  const [showAddress, setShowAddress] = useState(false);
  const [showObservations, setShowObservations] = useState(false);
  const width = useWindowWidth();
  const isMobile = width < 768;

  const statusOptions = [
    { id: 'Ativo', label: 'Ativo', color: 'success' },
    { id: 'Inativo', label: 'Inativo', color: 'danger' },
  ];

  useEffect(() => {
    if (customer) {
      setFormData({
        ...customer,
        phones: customer.phones || [],
        address: customer.address || {
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
          zip_code: '',
        },
      });
    }
  }, [customer]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value,
        },
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePhoneChange = (index, e) => {
    const newPhones = [...formData.phones];
    newPhones[index] = e.target.value;
    setFormData({ ...formData, phones: newPhones });
  };

  const addPhone = () => {
    setFormData({ ...formData, phones: [...formData.phones, ''] });
  };

  const removePhone = (index) => {
    const newPhones = formData.phones.filter((_, i) => i !== index);
    setFormData({ ...formData, phones: newPhones });
  };

  const handleStatusClick = (status) => {
    setFormData({ ...formData, status });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const method = customer ? 'put' : 'post';
    const url = customer ? `/customers/${customer.id}` : '/customers';

    api[method](url, formData)
      .then(() => {
        swalSuccess('Cliente salvo com sucesso!');
        onSuccess();
      })
      .catch(error => {
        console.error('Houve um erro ao salvar o cliente!', error);
        swalError('Erro ao salvar cliente.');
      });
  };

  // ════════════════════════════════════
  // MOBILE FORM
  // ════════════════════════════════════
  const renderMobileForm = () => (
    <form onSubmit={handleSubmit} className="expense-form-mobile" autoComplete="off">

      {/* Nome */}
      <div className="form-section">
        <label className="section-label">Nome / Razão Social</label>
        <input
          type="text"
          className="form-control form-control-lg"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Nome ou razão social"
          required
        />
      </div>

      {/* Nome Fantasia */}
      <div className="form-section">
        <label className="section-label">Nome Fantasia (Opcional)</label>
        <input
          type="text"
          className="form-control"
          name="trading_name"
          value={formData.trading_name}
          onChange={handleChange}
          placeholder="Nome fantasia"
        />
      </div>

      {/* Documento + Email */}
      <div className="form-section">
        <div className="row g-2">
          <div className="col-6">
            <label className="section-label">Documento</label>
            <input
              type="text"
              className="form-control"
              name="document"
              value={formData.document}
              onChange={handleChange}
              placeholder="CPF ou CNPJ"
              required
            />
          </div>
          <div className="col-6">
            <label className="section-label">Email (Opcional)</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="email@..."
            />
          </div>
        </div>
      </div>

      {/* Telefones */}
      <div className="form-section">
        <label className="section-label">Telefones (Opcional)</label>
        {formData.phones.map((phone, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
            <input
              type="tel"
              className="form-control"
              value={phone}
              onChange={(e) => handlePhoneChange(index, e)}
              placeholder="(00) 00000-0000"
            />
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removePhone(index)}>X</button>
          </div>
        ))}
        <button type="button" className="btn btn-outline-primary btn-sm w-100 mt-2" onClick={addPhone}>
          + Adicionar Telefone
        </button>
      </div>

      {/* Endereço - Expansível */}
      <div className="form-section">
        <button type="button" className="expand-btn" onClick={() => setShowAddress(!showAddress)}>
          <span>Endereço (Opcional)</span>
          <span className="expand-icon">{showAddress ? '−' : '+'}</span>
        </button>
        {showAddress && (
          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e9ecef' }}>
            <div className="row g-2 mb-2">
              <div className="col-8">
                <input type="text" className="form-control" name="address.street" value={formData.address.street} onChange={handleChange} placeholder="Rua" />
              </div>
              <div className="col-4">
                <input type="text" className="form-control" name="address.number" value={formData.address.number} onChange={handleChange} placeholder="Nº" />
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-6">
                <input type="text" className="form-control" name="address.complement" value={formData.address.complement} onChange={handleChange} placeholder="Complemento" />
              </div>
              <div className="col-6">
                <input type="text" className="form-control" name="address.neighborhood" value={formData.address.neighborhood} onChange={handleChange} placeholder="Bairro" />
              </div>
            </div>
            <div className="row g-2">
              <div className="col-6">
                <input type="text" className="form-control" name="address.city" value={formData.address.city} onChange={handleChange} placeholder="Cidade" />
              </div>
              <div className="col-2">
                <input type="text" className="form-control" name="address.state" value={formData.address.state} onChange={handleChange} placeholder="UF" maxLength="2" />
              </div>
              <div className="col-4">
                <input type="text" className="form-control" name="address.zip_code" value={formData.address.zip_code} onChange={handleChange} placeholder="CEP" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Observações - Expansível */}
      <div className="form-section">
        <button type="button" className="expand-btn" onClick={() => setShowObservations(!showObservations)}>
          <span>Observações (Opcional)</span>
          <span className="expand-icon">{showObservations ? '−' : '+'}</span>
        </button>
        {showObservations && (
          <textarea
            className="form-control mt-2"
            name="observations"
            value={formData.observations}
            onChange={handleChange}
            rows="3"
            placeholder="Adicione observações..."
          />
        )}
      </div>

      {/* Status */}
      <div className="form-section">
        <label className="section-label">Status</label>
        <div className="status-buttons">
          {statusOptions.map(s => (
            <button
              type="button"
              key={s.id}
              className={`status-btn ${s.color} ${formData.status === s.id ? 'active' : ''}`}
              onClick={() => handleStatusClick(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mobile-form-footer">
        <button type="submit" className="btn btn-primary btn-lg w-100">
          Salvar Cliente
        </button>
      </div>
    </form>
  );

  // ════════════════════════════════════
  // DESKTOP FORM
  // ════════════════════════════════════
  const renderDesktopForm = () => (
    <form onSubmit={handleSubmit} className="rv-form-desktop">

      {/* Nome + Nome Fantasia */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="name">Nome / Razão Social</label>
          <input type="text" className="rv-form-input" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Nome ou razão social" required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="trading_name">Nome Fantasia (Opcional)</label>
          <input type="text" className="rv-form-input" id="trading_name" name="trading_name" value={formData.trading_name} onChange={handleChange} placeholder="Nome fantasia" />
        </div>
      </div>

      {/* Documento + Email */}
      <div className="rv-form-grid-2">
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="document">Documento (CPF/CNPJ)</label>
          <input type="text" className="rv-form-input" id="document" name="document" value={formData.document} onChange={handleChange} placeholder="000.000.000-00" required />
        </div>
        <div className="rv-form-field">
          <label className="rv-form-label" htmlFor="email">Email (Opcional)</label>
          <input type="email" className="rv-form-input" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="email@exemplo.com" />
        </div>
      </div>

      {/* Telefones */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Telefones (Opcional)</label>
        {formData.phones.map((phone, index) => (
          <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <input type="tel" className="rv-form-input" value={phone} onChange={(e) => handlePhoneChange(index, e)} placeholder="(00) 00000-0000" />
            <button type="button" className="rv-btn-delete" onClick={() => removePhone(index)}>Remover</button>
          </div>
        ))}
        <button type="button" className="rv-btn rv-btn-outline" onClick={addPhone}>+ Adicionar Telefone</button>
      </div>

      {/* Endereço - Expansível */}
      <div className="rv-form-field-full">
        <button type="button" className="expand-btn" onClick={() => setShowAddress(!showAddress)}>
          <span>Endereço (Opcional)</span>
          <span className="expand-icon">{showAddress ? '−' : '+'}</span>
        </button>
        {showAddress && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="rv-form-grid-2" style={{ marginBottom: '0.75rem' }}>
              <div className="rv-form-field">
                <label className="rv-form-label">Rua</label>
                <input type="text" className="rv-form-input" name="address.street" value={formData.address.street} onChange={handleChange} placeholder="Nome da rua" />
              </div>
              <div className="rv-form-field">
                <label className="rv-form-label">Número</label>
                <input type="text" className="rv-form-input" name="address.number" value={formData.address.number} onChange={handleChange} placeholder="Nº" />
              </div>
            </div>
            <div className="rv-form-grid-2" style={{ marginBottom: '0.75rem' }}>
              <div className="rv-form-field">
                <label className="rv-form-label">Complemento</label>
                <input type="text" className="rv-form-input" name="address.complement" value={formData.address.complement} onChange={handleChange} placeholder="Apto, Bloco..." />
              </div>
              <div className="rv-form-field">
                <label className="rv-form-label">Bairro</label>
                <input type="text" className="rv-form-input" name="address.neighborhood" value={formData.address.neighborhood} onChange={handleChange} />
              </div>
            </div>
            <div className="rv-form-grid-3">
              <div className="rv-form-field">
                <label className="rv-form-label">Cidade</label>
                <input type="text" className="rv-form-input" name="address.city" value={formData.address.city} onChange={handleChange} />
              </div>
              <div className="rv-form-field">
                <label className="rv-form-label">Estado</label>
                <input type="text" className="rv-form-input" name="address.state" value={formData.address.state} onChange={handleChange} maxLength="2" />
              </div>
              <div className="rv-form-field">
                <label className="rv-form-label">CEP</label>
                <input type="text" className="rv-form-input" name="address.zip_code" value={formData.address.zip_code} onChange={handleChange} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Observações */}
      <div className="rv-form-field-full">
        <label className="rv-form-label" htmlFor="observations">Observações (Opcional)</label>
        <textarea className="rv-form-textarea" id="observations" name="observations" value={formData.observations} onChange={handleChange} placeholder="Adicione observações..." />
      </div>

      {/* Status */}
      <div className="rv-form-field-full">
        <label className="rv-form-label">Status</label>
        <div className="rv-status-group">
          {statusOptions.map(s => (
            <button
              type="button"
              key={s.id}
              className={`rv-status-toggle ${formData.status === s.id ? `active-${s.color}` : ''}`}
              onClick={() => handleStatusClick(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="rv-form-footer">
        <button type="submit" className="rv-btn-submit">Salvar Cliente</button>
      </div>
    </form>
  );

  return isMobile ? renderMobileForm() : renderDesktopForm();
}

export default CustomerForm;
